import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useToast } from './Toast';
import { IconEye } from './Icons';

function countdown(msLeft: number): string {
  const total = Math.max(0, Math.floor(msLeft / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * The one piece of deliberately loud chrome in the whole application.
 *
 * Acting as another person is the single most consequential state an
 * operator can be in — every action they take is attributed to that user
 * and is being written to the audit log. It is worth a full-width amber bar
 * that cannot be dismissed, pinned above everything, with the remaining
 * time visible: the token lasts 30 minutes and cannot be refreshed, so an
 * operator should never be surprised by it lapsing mid-task.
 */
export function ImpersonationBanner() {
  const { impersonation, stopImpersonation } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    if (!impersonation) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [impersonation]);

  if (!impersonation) return null;

  const msLeft = impersonation.expiresAt - now;
  const expiringSoon = msLeft < 5 * 60 * 1000;

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopImpersonation();
      notify(`Stopped acting as ${impersonation.fullName}`, 'success');
      navigate('/users', { replace: true });
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-live/30 bg-live-wash px-4 py-2 text-[0.8125rem]">
      <span className="flex items-center gap-2 font-medium text-live-text">
        <IconEye className="size-4" />
        Acting as {impersonation.fullName}
      </span>

      <span className="text-ink-2">
        {impersonation.email} · {impersonation.role}
      </span>

      <span
        className={`tabular ml-auto ${expiringSoon ? 'font-medium text-danger' : 'text-ink-3'}`}
        title="Impersonation tokens last 30 minutes and cannot be extended"
      >
        {msLeft > 0 ? `${countdown(msLeft)} left` : 'Expired'}
      </span>

      <button
        type="button"
        className="btn btn-sm btn-secondary"
        onClick={handleStop}
        disabled={stopping}
      >
        {stopping ? 'Stopping…' : 'Stop and return to admin'}
      </button>
    </div>
  );
}
